<script lang="ts">
  import { onMount } from 'svelte';
  import {
    getBusinessConfig, updateBusinessConfig, type BusinessConfig,
    getEmailPrefs, setEmailPrefs, type EmailPrefs,
  } from '../lib/api';
  // `agentName` went with the subtitle's Teach George pointer — this page no
  // longer names the agent anywhere. (`currentView` is still used by the
  // "View in Contacts →" signpost.)
  import { currentView, terms, stageLabel, startSync, userEmail, appTheme,
    themePreference, saveTheme, type AppTheme } from '../lib/stores';
  import SettingsBlock from '../lib/SettingsBlock.svelte';
  import SettingsRow from '../lib/SettingsRow.svelte';
  import { getFundWorkspace, setFundPersonalEmails, setFundAutoDrafts } from '../lib/api/fund-ir';
  import SyncButton from '../lib/SyncButton.svelte';
  import { isSupportView } from '../lib/supportView';
  import type { FundWorkspace } from '../lib/api/fund-ir';

  // The wipe actions live in App.svelte because they reset onboarding state
  // that App owns. Settings only renders the buttons. `canWipe` carries the
  // same hardcoded-email gate the sidebar used before the move.
  let { canWipe = false, onWipeData, onWipeOnboard, workflow = 'sales',
    onLogout,
    showTitle = true }: {
    canWipe?: boolean;
    onWipeData?: () => void;
    onWipeOnboard?: () => void;
    workflow?: 'sales' | 'fund';
    onLogout?: () => void | Promise<void>;
    showTitle?: boolean;
  } = $props();
  let fundWorkspace = $state<FundWorkspace | null>(null);
  let fundLoadFailed = $state(false);
  let fundPersonalSaving = $state(false);
  let fundAutoSaving = $state(false);
  const connectUrl = $derived(fundWorkspace?.provider === 'microsoft'
    ? '/api/auth/microsoft/login' : '/api/auth/login?reauth=true');

  // Platform-sent email toggles (default enabled until loaded).
  let emailPrefs = $state<EmailPrefs>({ george_messages: true, pre_call_briefs: true });
  let toast = $state('');

  // Business config is loaded here for ONE field: the personal-email scope
  // rule. Everything else on that record is edited in Teach George. We send
  // ONLY our own key on save — the backend upserts per key, so a partial body
  // leaves the profile fields untouched. Sending {...biz} instead would write
  // this page's stale snapshot over any edit made in Teach George.
  let biz = $state<BusinessConfig | null>(null);
  let bizSaving = $state(false);
  // Distinguishes "still loading" from "the GET failed". Without it a failed
  // load left the toggle `disabled` forever with nothing on screen to say why.
  let bizLoadFailed = $state(false);

  // Reads the stored field directly — ON means `personal_emails_as_clients`
  // is true, no inversion. The mockup phrased this as "treated as Non-Clients"
  // (ON = false), but both the previous Settings row and Onboarding ask it the
  // positive way, and a toggle whose ON state means "don't" inverts twice.
  // Unset/absent = OFF = stay in Non-Clients, which is the existing default.
  const personalAsProspects = $derived(
    biz ? String(biz.personal_emails_as_clients).toLowerCase() === 'true' : false
  );

  // Every write below follows the same rule, because a browser-driven audit
  // caught all of them stranding the page: `finally` always clears the busy
  // flag, a failure re-reads the stored value rather than leaving the control
  // showing a state that was never saved, and the user is told. Before this,
  // aborting the PUT left the switch ON *and* `disabled` permanently — the
  // control was bricked until reload, silently.
  // Takes the ELEMENT, not the boolean, because reverting has to be done to the
  // DOM node by hand. `checked={personalAsProspects}` is a one-way attribute:
  // clicking changes the DOM but not the state, so on failure the derived value
  // recomputes to what it already was, Svelte sees no change, skips the DOM
  // update — and the switch keeps the user's click while nothing was saved.
  // (Dirtying `biz` does NOT help, for the same reason. Measured: the first
  // attempt at this fix left the switch ON with `'false'` in the database.)
  async function savePersonalEmails(el: HTMLInputElement) {
    if (!biz) return;
    const asProspects = el.checked;
    bizSaving = true;
    try {
      await updateBusinessConfig({ personal_emails_as_clients: asProspects ? 'true' : 'false' });
      biz = await getBusinessConfig();
      showToast(`Personal emails treated as ${asProspects ? $terms.prospect_plural : $stageLabel('Non-Clients')}`);
    } catch (err: any) {
      el.checked = personalAsProspects;   // the stored value, untouched by the click
      showToast('Could not save: ' + (err?.message || 'request failed'), true);
    } finally {
      bizSaving = false;
    }
  }

  async function saveEmailPref(key: keyof EmailPrefs, value: boolean) {
    const previous = emailPrefs[key];
    emailPrefs[key] = value;
    try {
      await setEmailPrefs({ [key]: value });
      showToast(`${key === 'george_messages' ? 'Sales Partner Communication' : 'Call Briefs'} ${value ? 'enabled' : 'disabled'}`);
    } catch (err: any) {
      // Without this it flips and stays flipped: abort the PUT and the
      // page showed OFF while the server still said ON, with no message.
      emailPrefs[key] = previous;
      showToast('Could not save: ' + (err?.message || 'request failed'), true);
    }
  }

  async function saveFundPersonalEmails(value: boolean) {
    if (!fundWorkspace || fundPersonalSaving) return;
    fundPersonalSaving = true;
    try {
      await setFundPersonalEmails(value);
      fundWorkspace = await getFundWorkspace();
      showToast(value ? 'Personal-domain contacts included. Sync will rescan your mailbox.' : 'Personal-domain email context is hidden for your account.');
      if (value && fundWorkspace.inbox_connected) startSync();
    } catch (err: any) {
      fundWorkspace = await getFundWorkspace().catch(() => fundWorkspace);
      showToast('Could not save: ' + (err?.message || 'request failed'), true);
    } finally {
      fundPersonalSaving = false;
    }
  }

  async function saveFundAutoDrafts(el: HTMLInputElement) {
    if (!fundWorkspace || fundAutoSaving) return;
    const enabled = el.checked;
    const previous = fundWorkspace.preferences.auto_drafts.enabled;
    fundAutoSaving = true;
    try {
      const saved = await setFundAutoDrafts(enabled, fundWorkspace.preferences.auto_draft_consent_version);
      fundWorkspace.preferences.auto_drafts = saved;
      showToast(enabled ? 'Review drafts enabled. New drafts will appear in Today.'
        : 'Automatic drafting stopped. Existing review drafts remain available.');
    } catch (err: any) {
      el.checked = previous;
      showToast('Could not save: ' + (err?.message || 'request failed'), true);
    } finally {
      fundAutoSaving = false;
    }
  }

  // Failures must render via `.toast-error`, never the success box:
  // DESIGN-SYSTEM §1 reserves green for on-track status.
  let toastError = $state(false);
  function showToast(msg: string, isError = false) {
    toast = msg;
    toastError = isError;
    setTimeout(() => toast = '', isError ? 5000 : 3000);
  }

  onMount(async () => {
    if (workflow === 'fund') {
      try {
        fundWorkspace = await getFundWorkspace();
      } catch {
        fundLoadFailed = true;
      }
      return;
    }
    const [bizData, eprefs] = await Promise.all([
      getBusinessConfig().catch(() => null),
      getEmailPrefs().catch(() => ({ george_messages: true, pre_call_briefs: true })),
    ]);
    biz = bizData;
    bizLoadFailed = bizData === null;
    emailPrefs = eprefs;
  });
</script>

{#if showTitle}<div class="page-title">Settings</div>{/if}
<!-- Subtitles stay focused on their own page. Do NOT re-add a "…lives in Teach George"
     pointer here: it was added when
     MOVED Business Profile and Email Personas off Settings. Removed for the
     same reason the signpost ROW was: it duplicates a sidebar item sitting
     directly above Settings.
     ⚠️ That was the last in-app wayfinding for the move. Anyone with muscle
     memory for Business Profile on Settings now has only the sidebar to go on. -->
<div class="page-subtitle">How Dealton behaves for you.</div>

<!-- ORDER IS LOAD-BEARING — the `PIPELINE SCOPE` / `THIS APP` group headers were
     removed (a group of one card, and "this app" named nothing; Call settings,
     which this card pattern comes from, uses 5 blocks and no groups). With them
     gone, the SEQUENCE is the only thing left saying which cards are about
     Dealton and which are about you. It is:
       preferences before terminal actions — nothing that ends a session or
       erases data sits above a toggle;
       within preferences, most-reached-for first — Notifications is why people
       open this page; Prospect detection is set once; Appearance is a nicety;
       within actions, reversible before not — Sign out, then wipe.
     Don't reorder casually. -->

{#if workflow === 'sales'}
<SettingsBlock title="Notifications">
  <SettingsRow label="Sales partner communication">
    {#snippet hint()}Pipeline analysis and strategy on key accounts. {emailPrefs.george_messages
      ? 'Sent to your inbox, and shown in Today.' : 'Shown in Today only, not emailed.'}{/snippet}
    {#snippet control()}
      <label class="toggle-switch">
        <input type="checkbox" role="switch" aria-label="Sales partner communication emails"
          checked={emailPrefs.george_messages}
          onchange={(e) => saveEmailPref('george_messages', (e.currentTarget as HTMLInputElement).checked)} />
        <span class="toggle-slider"></span>
      </label>
    {/snippet}
  </SettingsRow>
  <SettingsRow label="Call briefs">
    {#snippet hint()}Call preparation documents for upcoming calls. {emailPrefs.pre_call_briefs
      ? 'Sent to your inbox, and shown in Today.' : 'Shown in Today only, not emailed.'}{/snippet}
    {#snippet control()}
      <label class="toggle-switch">
        <input type="checkbox" role="switch" aria-label="Call brief emails"
          checked={emailPrefs.pre_call_briefs}
          onchange={(e) => saveEmailPref('pre_call_briefs', (e.currentTarget as HTMLInputElement).checked)} />
        <span class="toggle-slider"></span>
      </label>
    {/snippet}
  </SettingsRow>
</SettingsBlock>
{:else if fundLoadFailed}
<SettingsBlock title="Inbox">
  <p class="loadfail">Could not load your Fund account settings. Reload to try again.</p>
</SettingsBlock>
{:else if fundWorkspace}
<SettingsBlock title="Inbox">
  <SettingsRow label={`${fundWorkspace.provider === 'microsoft' ? 'Microsoft' : 'Google'} mailbox`}>
    {#snippet hint()}{fundWorkspace?.inbox_connected ? 'Connected. Synced business relationship mail is available to your Fund team. Confirm an LP before generating a brief or draft; generation never sends an email.' : 'Connect your mailbox to find Fund relationships. Synced business relationship mail is available to your Fund team.'}{/snippet}
    {#snippet control()}{#if fundWorkspace?.inbox_connected}<SyncButton />{:else if isSupportView()}<span class="text-dim">Not connected</span>{:else}<a href={connectUrl} class="today-mini-btn">Connect inbox</a>{/if}{/snippet}
  </SettingsRow>
  <SettingsRow label="Include personal email domains">
    {#snippet hint()}Include addresses such as Gmail and Outlook in your own Fund mailbox scan. Turning this off hides previously synced personal-domain context without deleting it.{/snippet}
    {#snippet control()}<label class="toggle-switch"><input type="checkbox" role="switch" aria-label="Include personal email domains" checked={fundWorkspace?.preferences.include_personal_emails ?? false} disabled={fundPersonalSaving} onchange={(e) => saveFundPersonalEmails((e.currentTarget as HTMLInputElement).checked)} /><span class="toggle-slider"></span></label>{/snippet}
  </SettingsRow>
  <SettingsRow label="Automatic review drafts">
    {#snippet hint()}
      Dealton prepares review-only drafts for confirmed LPs after a direct reply or follow-up signal. Relevant mailbox excerpts may be processed by Anthropic; no Fund documents are included. You choose whether to send each draft. Turning this off stops future generation; existing drafts remain. <a href="https://dealton.ai/terms.html" target="_blank" rel="noopener">Terms</a> · <a href="https://dealton.ai/privacy.html" target="_blank" rel="noopener">Privacy</a>.
    {/snippet}
    {#snippet control()}
      <label class="toggle-switch"><input type="checkbox" role="switch" aria-label="Automatic review drafts" checked={fundWorkspace?.preferences.auto_drafts.enabled ?? false} disabled={fundAutoSaving} onchange={(e) => saveFundAutoDrafts(e.currentTarget as HTMLInputElement)} /><span class="toggle-slider"></span></label>
    {/snippet}
  </SettingsRow>
</SettingsBlock>
{/if}

<!-- Stays on Settings, NOT Teach George: by the spec's own test (§27, "does this
     describe George, or describe Dealton?") it describes Dealton — it decides
     which senders become CLIENTS, which moves Contacts, Dashboard and Today.
     George never sees it; it is consumed by the poller at ingestion time
     (gmail_poller.py:1180). Filing it under Teach George would re-make the exact
     miscategorisation of putting it inside Business Profile. -->
{#if workflow === 'sales'}
<SettingsBlock title={`${$stageLabel('Prospect')} detection`}>
  <SettingsRow label={`Treat personal emails as ${$terms.prospect_plural}`}>
    {#snippet hint()}Addresses on personal domains like Gmail, Outlook and iCloud, rather than a company domain.{/snippet}
    {#snippet extra()}{#if bizLoadFailed}<div class="loadfail">Couldn't load this setting. Reload the page to try again.</div>{/if}{/snippet}
    {#snippet control()}
      <label class="toggle-switch">
        <input type="checkbox" role="switch" aria-label={`Treat personal emails as ${$terms.prospect_plural}`}
          checked={personalAsProspects} disabled={!biz || bizSaving}
          onchange={(e) => savePersonalEmails(e.currentTarget as HTMLInputElement)} />
        <span class="toggle-slider"></span>
      </label>
    {/snippet}
  </SettingsRow>
  <SettingsRow label="Where senders ended up">
    {#snippet hint()}Contacts groups every sender by stage, {$stageLabel('Non-Clients')} included.{/snippet}
    {#snippet control()}<button class="navlink" onclick={() => currentView.set('contacts')}>View in Contacts →</button>{/snippet}
  </SettingsRow>
</SettingsBlock>
{/if}

<SettingsBlock title="Appearance">
  <SettingsRow label="Theme">
    {#snippet hint()}Applies to this device only.{#if $themePreference === 'auto'} Following your system setting ({$appTheme}).{/if}{/snippet}
    {#snippet control()}
      <div class="seg">
        {#each [['light', 'Light'], ['dark', 'Dark'], ['auto', 'Auto']] as [value, label]}
          <button type="button" class:on={$themePreference === value} onclick={() => saveTheme(value as AppTheme)}>{label}</button>
        {/each}
      </div>
    {/snippet}
  </SettingsRow>
</SettingsBlock>
<SettingsBlock title="Account">
  <SettingsRow label={$userEmail}>
    {#snippet hint()}{workflow === 'fund' && fundWorkspace?.profile.manager_name
      ? `Signed in to ${fundWorkspace.profile.manager_name}` : 'Signed in'}{/snippet}
    {#snippet control()}
      <button type="button" class="today-mini-btn" onclick={onLogout ?? (() => { fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).then(() => window.location.reload()); })}>Sign out</button>
    {/snippet}
  </SettingsRow>
</SettingsBlock>

<!-- "Danger zone" not "Data": Admin.svelte already uses that term for exactly
     this, and "Data" understates two irreversible buttons. Row labels describe,
     buttons carry the verb — otherwise both say the same words twice. -->
<!-- `canWipe` is the whole gate. App.svelte decides it — Dealton staff, or dev —
     and owns the handlers, so this component only renders buttons.
     🔴 It is NOT authorization. `POST /api/poller/wipe` and `POST /api/onboarding/reset`
     each enforce their own, and the wipe route only acquired one when this block came
     back: before that it had no gate beyond being signed in, and was unreachable merely
     because the markup was compiled out. Those two gates are the reason it is safe for
     this markup to exist in a production bundle at all.
     See `_backend/tests/test_destructive_route_authz.py`. -->
{#if workflow === 'sales' && canWipe}
  <SettingsBlock title="Danger zone">
    <SettingsRow label="Erase and rebuild your data">
      {#snippet hint()}Clears your Dealton email data and re-ingests it from scratch.{/snippet}
      {#snippet control()}<button class="today-mini-btn danger" onclick={() => onWipeData?.()}>Wipe</button>{/snippet}
    </SettingsRow>
    <SettingsRow label="Erase and start onboarding again">
      {#snippet hint()}Clears data and returns you to the first onboarding step.{/snippet}
      {#snippet control()}<button class="today-mini-btn danger" onclick={() => onWipeOnboard?.()}>Wipe &amp; restart</button>{/snippet}
    </SettingsRow>
  </SettingsBlock>
{/if}

<div class="legal">
  <a href="https://dealton.ai/privacy.html" target="_blank" rel="noopener">Privacy Policy</a>
  <a href="https://dealton.ai/terms.html" target="_blank" rel="noopener">Terms of Use</a>
</div>

{#if toast}
  <div class="toast" class:toast-success={!toastError} class:toast-error={toastError}>{toast}</div>
{/if}

<style>
  .today-mini-btn { background: var(--bg-hover); }
  .legal { display: flex; gap: 16px; margin: 14px 0 0 2px; }
  .legal a { font-size: 11.5px; color: var(--text-dim); text-decoration: none; }
  .legal a:hover { color: var(--text-muted); }
  .loadfail { margin-top: 2px; font-size: 11.5px; color: var(--red); line-height: 1.5; }
</style>
